export interface Point {
  x: number;
  y: number;
}

export interface SurfaceTransform {
  x: number;
  y: number;
  scale: number;
}

export interface CatalogSurfaceSnapshot extends SurfaceTransform {
  activeIndex: number;
  viewportWidth: number;
  viewportHeight: number;
  pageWidth: number;
  pageHeight: number;
  pageGap: number;
}

export interface CatalogSurfaceOptions {
  viewport: HTMLElement;
  stage: HTMLElement;
  pageCount: number;
  pageAspectWidth: number;
  pageAspectHeight: number;
  initialIndex: number;
  minScale?: number;
  maxScale?: number;
  doubleTapScale?: number;
  onPageChange(index: number): void;
  onTransformChange?(snapshot: CatalogSurfaceSnapshot): void;
  onTap(): void;
}

interface PointerSample extends Point {
  id: number;
}

interface PanStart {
  pointerId: number;
  point: Point;
  x: number;
  y: number;
}

interface PinchStart {
  pointerIds: [number, number];
  distance: number;
  scale: number;
  contentX: number;
  contentY: number;
}

const TAP_DELAY_MS = 260;
const DOUBLE_TAP_DISTANCE = 32;
const MOVE_THRESHOLD = 5;
const PAGE_GAP = 1;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function distanceBetween(first: Point, second: Point): number {
  return Math.hypot(second.x - first.x, second.y - first.y);
}

function midpoint(first: Point, second: Point): Point {
  return {
    x: first.x + (second.x - first.x) / 2,
    y: first.y + (second.y - first.y) / 2,
  };
}

export function fitPage(
  viewportWidth: number,
  viewportHeight: number,
  pageWidth: number,
  pageHeight: number,
): { width: number; height: number } {
  const pageRatio = pageWidth / pageHeight;
  const viewportRatio = viewportWidth / viewportHeight;
  if (viewportRatio > pageRatio) {
    return { width: viewportHeight * pageRatio, height: viewportHeight };
  }
  return { width: viewportWidth, height: viewportWidth / pageRatio };
}

export function zoomAroundPoint(
  current: SurfaceTransform,
  nextScale: number,
  focalPoint: Point,
): SurfaceTransform {
  const contentX = (focalPoint.x - current.x) / current.scale;
  const contentY = (focalPoint.y - current.y) / current.scale;
  return {
    x: focalPoint.x - contentX * nextScale,
    y: focalPoint.y - contentY * nextScale,
    scale: nextScale,
  };
}

export function pageIndexAtCenter(
  viewportCenterX: number,
  translateX: number,
  scale: number,
  pageWidth: number,
  pageGap: number,
  pageCount: number,
): number {
  const contentX = (viewportCenterX - translateX) / scale;
  const rawIndex = (contentX - pageWidth / 2) / (pageWidth + pageGap);
  return clamp(Math.round(rawIndex), 0, pageCount - 1);
}

export class CatalogSurfaceController {
  private readonly viewport: HTMLElement;
  private readonly stage: HTMLElement;
  private readonly pageCount: number;
  private readonly pageAspectWidth: number;
  private readonly pageAspectHeight: number;
  private readonly minScale: number;
  private readonly maxScale: number;
  private readonly doubleTapScale: number;
  private readonly onPageChange: (index: number) => void;
  private readonly onTransformChange: (
    snapshot: CatalogSurfaceSnapshot,
  ) => void;
  private readonly onTap: () => void;
  private readonly pages: HTMLElement[];
  private readonly pointers = new Map<number, PointerSample>();
  private readonly resizeObserver: ResizeObserver;

  private viewportWidth = 1;
  private viewportHeight = 1;
  private pageWidth = 1;
  private pageHeight = 1;
  private stageWidth = 1;
  private transform: SurfaceTransform = { x: 0, y: 0, scale: 1 };
  private currentIndex: number;
  private panStart: PanStart | null = null;
  private pinchStart: PinchStart | null = null;
  private gestureMoved = false;
  private gestureUsedPinch = false;
  private lastTapAt = 0;
  private lastTapPoint: Point | null = null;
  private tapTimer: number | undefined;

  constructor(options: CatalogSurfaceOptions) {
    this.viewport = options.viewport;
    this.stage = options.stage;
    this.pageCount = options.pageCount;
    this.pageAspectWidth = options.pageAspectWidth;
    this.pageAspectHeight = options.pageAspectHeight;
    this.minScale = options.minScale ?? 1;
    this.maxScale = options.maxScale ?? 4;
    this.doubleTapScale = options.doubleTapScale ?? 2.5;
    this.onPageChange = options.onPageChange;
    this.onTransformChange = options.onTransformChange ?? (() => undefined);
    this.onTap = options.onTap;
    this.currentIndex = clamp(options.initialIndex, 0, this.pageCount - 1);
    this.pages = [
      ...this.stage.querySelectorAll<HTMLElement>("[data-catalog-page]"),
    ];

    this.viewport.addEventListener("pointerdown", this.handlePointerDown);
    this.viewport.addEventListener("pointermove", this.handlePointerMove);
    this.viewport.addEventListener("pointerup", this.handlePointerEnd);
    this.viewport.addEventListener("pointercancel", this.handlePointerCancel);
    this.viewport.addEventListener("lostpointercapture", this.handleLostCapture);
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.viewport);

    this.measure(false);
    this.goToPage(this.currentIndex, false);
  }

  get activeIndex(): number {
    return this.currentIndex;
  }

  get scale(): number {
    return this.transform.scale;
  }

  get translateX(): number {
    return this.transform.x;
  }

  get translateY(): number {
    return this.transform.y;
  }

  goToPage(index: number, animate = true): void {
    const nextIndex = clamp(index, 0, this.pageCount - 1);
    const pageCenter =
      nextIndex * (this.pageWidth + PAGE_GAP) + this.pageWidth / 2;
    this.transform.x = this.viewportWidth / 2 - pageCenter * this.transform.scale;
    this.applyTransform(animate, nextIndex);
  }

  zoomTo(scale: number, focalPoint: Point, animate = false): void {
    const nextScale = clamp(scale, this.minScale, this.maxScale);
    this.transform = zoomAroundPoint(this.transform, nextScale, focalPoint);
    this.applyTransform(animate);
  }

  destroy(): void {
    window.clearTimeout(this.tapTimer);
    this.resizeObserver.disconnect();
    this.viewport.removeEventListener("pointerdown", this.handlePointerDown);
    this.viewport.removeEventListener("pointermove", this.handlePointerMove);
    this.viewport.removeEventListener("pointerup", this.handlePointerEnd);
    this.viewport.removeEventListener("pointercancel", this.handlePointerCancel);
    this.viewport.removeEventListener("lostpointercapture", this.handleLostCapture);
  }

  private readonly handleResize = (): void => {
    this.measure(true);
  };

  private measure(preservePosition: boolean): void {
    const oldWidth = this.viewportWidth;
    const oldHeight = this.viewportHeight;
    const oldPageWidth = this.pageWidth;
    const oldPageHeight = this.pageHeight;
    const oldStep = oldPageWidth + PAGE_GAP;
    const oldContentX = (oldWidth / 2 - this.transform.x) / this.transform.scale;
    const oldPageOffset = oldContentX - this.currentIndex * oldStep;
    const oldPageFraction = oldPageWidth > 0 ? oldPageOffset / oldPageWidth : 0.5;
    const oldContentY = (oldHeight / 2 - this.transform.y) / this.transform.scale;
    const oldVerticalFraction = oldPageHeight > 0 ? oldContentY / oldPageHeight : 0.5;

    this.viewportWidth = Math.max(this.viewport.clientWidth, 1);
    this.viewportHeight = Math.max(this.viewport.clientHeight, 1);
    const fittedPage = fitPage(
      this.viewportWidth,
      this.viewportHeight,
      this.pageAspectWidth,
      this.pageAspectHeight,
    );
    this.pageWidth = fittedPage.width;
    this.pageHeight = fittedPage.height;
    this.stageWidth =
      this.pageCount * this.pageWidth + (this.pageCount - 1) * PAGE_GAP;
    this.stage.style.setProperty("--catalog-page-width", `${this.pageWidth}px`);
    this.stage.style.setProperty("--catalog-page-height", `${this.pageHeight}px`);
    this.stage.style.setProperty("--catalog-page-gap", `${PAGE_GAP}px`);
    this.stage.style.width = `${this.stageWidth}px`;
    this.stage.style.height = `${this.pageHeight}px`;

    if (preservePosition) {
      const nextContentX =
        this.currentIndex * (this.pageWidth + PAGE_GAP) +
        oldPageFraction * this.pageWidth;
      const nextContentY = oldVerticalFraction * this.pageHeight;
      this.transform.x =
        this.viewportWidth / 2 - nextContentX * this.transform.scale;
      this.transform.y =
        this.viewportHeight / 2 - nextContentY * this.transform.scale;
      this.applyTransform(false);
    }
  }

  private boundsFor(scale: number): {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } {
    const scaledPageWidth = this.pageWidth * scale;
    const horizontalMargin = Math.max(
      (this.viewportWidth - scaledPageWidth) / 2,
      0,
    );
    const scaledStageWidth = this.stageWidth * scale;
    const maxX = horizontalMargin;
    const minX = this.viewportWidth - scaledStageWidth - horizontalMargin;
    const scaledPageHeight = this.pageHeight * scale;
    if (scaledPageHeight <= this.viewportHeight) {
      const centeredY = (this.viewportHeight - scaledPageHeight) / 2;
      return { minX, maxX, minY: centeredY, maxY: centeredY };
    }
    return {
      minX,
      maxX,
      minY: this.viewportHeight - scaledPageHeight,
      maxY: 0,
    };
  }

  private applyTransform(animate: boolean, forcedIndex?: number): void {
    const bounds = this.boundsFor(this.transform.scale);
    this.transform.x = clamp(this.transform.x, bounds.minX, bounds.maxX);
    this.transform.y = clamp(this.transform.y, bounds.minY, bounds.maxY);
    this.stage.style.transitionDuration = animate ? "320ms" : "0ms";
    this.stage.style.transform = `translate3d(${this.transform.x}px,${this.transform.y}px,0) scale(${this.transform.scale})`;
    this.viewport.dataset.zoomScale = String(
      Number(this.transform.scale.toFixed(4)),
    );

    const nextIndex =
      forcedIndex ??
      pageIndexAtCenter(
        this.viewportWidth / 2,
        this.transform.x,
        this.transform.scale,
        this.pageWidth,
        PAGE_GAP,
        this.pageCount,
      );
    this.setActiveIndex(nextIndex);
    this.onTransformChange({
      ...this.transform,
      activeIndex: this.currentIndex,
      viewportWidth: this.viewportWidth,
      viewportHeight: this.viewportHeight,
      pageWidth: this.pageWidth,
      pageHeight: this.pageHeight,
      pageGap: PAGE_GAP,
    });
  }

  private setActiveIndex(index: number): void {
    const changed = index !== this.currentIndex;
    this.currentIndex = index;
    this.viewport.dataset.activePage = String(index + 1);
    this.pages.forEach((page, pageIndex) => {
      const active = pageIndex === index;
      page.classList.toggle("is-active", active);
      if (active) page.setAttribute("aria-current", "page");
      else page.removeAttribute("aria-current");
    });
    if (changed) this.onPageChange(index);
  }

  private pointFromEvent(event: PointerEvent): PointerSample {
    const rect = this.viewport.getBoundingClientRect();
    return {
      id: event.pointerId,
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  private readonly handlePointerDown = (event: PointerEvent): void => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const point = this.pointFromEvent(event);
    this.pointers.set(event.pointerId, point);
    this.stage.style.transitionDuration = "0ms";
    try {
      this.viewport.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic test events may not create a capturable browser pointer.
    }

    if (this.pointers.size === 1) {
      this.panStart = {
        pointerId: event.pointerId,
        point,
        x: this.transform.x,
        y: this.transform.y,
      };
      this.pinchStart = null;
      this.gestureMoved = false;
      this.gestureUsedPinch = false;
    } else if (this.pointers.size >= 2) {
      this.beginPinch();
      this.gestureUsedPinch = true;
    }
    if (event.cancelable) event.preventDefault();
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    if (!this.pointers.has(event.pointerId)) return;
    const point = this.pointFromEvent(event);
    this.pointers.set(event.pointerId, point);

    if (this.pointers.size >= 2) {
      const pair = this.activePointerPair();
      if (!pair) return;
      if (
        !this.pinchStart ||
        !this.pinchStart.pointerIds.every((id) => this.pointers.has(id))
      ) {
        this.beginPinch();
      }
      if (!this.pinchStart) return;
      const currentDistance = Math.max(distanceBetween(pair[0], pair[1]), 1);
      const nextScale = clamp(
        (this.pinchStart.scale * currentDistance) / this.pinchStart.distance,
        this.minScale,
        this.maxScale,
      );
      const currentMidpoint = midpoint(pair[0], pair[1]);
      this.transform = {
        x: currentMidpoint.x - this.pinchStart.contentX * nextScale,
        y: currentMidpoint.y - this.pinchStart.contentY * nextScale,
        scale: nextScale,
      };
      this.gestureMoved = true;
      this.applyTransform(false);
    } else if (this.panStart && this.panStart.pointerId === event.pointerId) {
      const deltaX = point.x - this.panStart.point.x;
      const deltaY = point.y - this.panStart.point.y;
      if (Math.hypot(deltaX, deltaY) > MOVE_THRESHOLD) this.gestureMoved = true;
      this.transform.x = this.panStart.x + deltaX;
      this.transform.y = this.panStart.y + deltaY;
      this.applyTransform(false);
    }
    if (event.cancelable) event.preventDefault();
  };

  private readonly handlePointerEnd = (event: PointerEvent): void => {
    const endedPoint = this.pointers.get(event.pointerId) ?? this.pointFromEvent(event);
    this.pointers.delete(event.pointerId);
    try {
      if (this.viewport.hasPointerCapture(event.pointerId)) {
        this.viewport.releasePointerCapture(event.pointerId);
      }
    } catch {
      // Pointer capture can already be released by the browser.
    }

    if (this.pointers.size >= 2) {
      this.beginPinch();
      return;
    }
    if (this.pointers.size === 1) {
      const remaining = [...this.pointers.values()][0];
      this.pinchStart = null;
      this.panStart = {
        pointerId: remaining.id,
        point: remaining,
        x: this.transform.x,
        y: this.transform.y,
      };
      return;
    }

    if (!this.gestureMoved && !this.gestureUsedPinch) this.registerTap(endedPoint);
    this.panStart = null;
    this.pinchStart = null;
    this.gestureMoved = false;
    this.gestureUsedPinch = false;
  };

  private readonly handlePointerCancel = (event: PointerEvent): void => {
    this.pointers.delete(event.pointerId);
    if (this.pointers.size === 0) {
      this.panStart = null;
      this.pinchStart = null;
      this.gestureMoved = false;
      this.gestureUsedPinch = false;
    }
  };

  private readonly handleLostCapture = (event: PointerEvent): void => {
    if (!this.pointers.has(event.pointerId)) return;
    this.handlePointerCancel(event);
  };

  private activePointerPair(): [PointerSample, PointerSample] | null {
    const pair = [...this.pointers.values()].slice(0, 2);
    return pair.length === 2 ? [pair[0], pair[1]] : null;
  }

  private beginPinch(): void {
    const pair = this.activePointerPair();
    if (!pair) return;
    const center = midpoint(pair[0], pair[1]);
    this.pinchStart = {
      pointerIds: [pair[0].id, pair[1].id],
      distance: Math.max(distanceBetween(pair[0], pair[1]), 1),
      scale: this.transform.scale,
      contentX: (center.x - this.transform.x) / this.transform.scale,
      contentY: (center.y - this.transform.y) / this.transform.scale,
    };
    this.panStart = null;
  }

  private registerTap(point: Point): void {
    const now = window.performance.now();
    const isDoubleTap =
      this.lastTapPoint !== null &&
      now - this.lastTapAt <= TAP_DELAY_MS &&
      distanceBetween(this.lastTapPoint, point) <= DOUBLE_TAP_DISTANCE;

    if (isDoubleTap) {
      window.clearTimeout(this.tapTimer);
      this.lastTapAt = 0;
      this.lastTapPoint = null;
      if (this.transform.scale > this.minScale + 0.01) {
        this.transform.scale = this.minScale;
        this.goToPage(this.currentIndex, true);
      } else {
        this.zoomTo(this.doubleTapScale, point, true);
      }
      return;
    }

    this.lastTapAt = now;
    this.lastTapPoint = point;
    window.clearTimeout(this.tapTimer);
    this.tapTimer = window.setTimeout(() => {
      this.lastTapAt = 0;
      this.lastTapPoint = null;
      this.onTap();
    }, TAP_DELAY_MS);
  }
}
